import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AtSign,
  BadgeCheck,
  Cog,
  ExternalLink,
  FileSignature,
  HelpCircle,
  Info,
  Mail,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { ShortcutButton } from "@/components/shortcut-button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useCurrentUser } from "@/hooks/use-current-user";
import { notify } from "@/lib/notify";

import type { ClaimWithRelations } from "../../api/use-claim";
import {
  useClaimContactEmails,
  useGenerateReceivables,
  useLaunchFamilyAcquisitionWorkflow,
  useNotifyReceivablesReady,
  usePnrContacts,
  useReceivableContracts,
} from "../../api/use-claim-actions";
import { findFamilyUsingEmail } from "../acquisition/shared-email";
import { SharedEmailConfirmDialog } from "../acquisition/shared-email-confirm-dialog";
import { PassengerQuestionsEditor } from "../passenger-questions/passenger-questions-editor";
import { FamilyBoard } from "../passengers/family-board";
import { groupPassengersByFamilies } from "../passengers/passenger-grouping";
import { PnrContacts } from "../passengers/pnr-contacts";
import { CtaShell } from "./cta-shell";

function isValidEmail(email: string | undefined): boolean {
  return Boolean(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
}

interface SignatureFormState {
  email: string;
  signerFullName: string;
  legalFirstName: string;
  legalLastName: string;
}

/**
 * A family whose passengers are all minors cannot sign for itself: a parent or guardian must,
 * and they are not a passenger. The form asks for their identity instead of a signer name.
 */
function isMinorsOnly(receivable: { passengers: Array<{ isAdult?: boolean }> }): boolean {
  return receivable.passengers.length > 0 && receivable.passengers.every(({ isAdult }) => !isAdult);
}

/** Passenger contact email → family name, so the PNR contacts can say which address is already in use. */
function familyByEmail(claim: ClaimWithRelations): Map<string, string> {
  const map = new Map<string, string>();
  for (const family of groupPassengersByFamilies(claim)) {
    for (const row of family) {
      if (row.passenger.contact && row.familyName) {
        map.set(row.passenger.contact.toLowerCase(), row.familyName);
      }
    }
  }
  return map;
}

type ProposableSigner = { fullName: string; isAdult?: boolean; email?: string | null };

/**
 * Who the cession is proposed to as signer.
 *
 * A minor cannot assign their claim, so the first passenger of the family is the wrong default
 * whenever a child happens to be listed first — the jurist would send "Child UNO" as the signer
 * of a family that also carries an adult. Falls back to the first passenger only when the family
 * has no adult at all, which the `minorsOnly` branch handles with its own fields anyway.
 */
function defaultSigner<T extends ProposableSigner>(receivable: { passengers: T[] }): T | undefined {
  return receivable.passengers.find(({ isAdult }) => isAdult) ?? receivable.passengers[0];
}

function defaultSignerFullName(receivable: { passengers: ProposableSigner[] }): string {
  return defaultSigner(receivable)?.fullName ?? "";
}

/**
 * What the form opens with — the single source of truth for both the inputs and the send, so a
 * jurist who touches nothing still sends what they were shown.
 *
 * A legal representative a passenger already supplied through the signature funnel arrives here: the FK moved
 * to the family, so it is readable before this acquisition exists (TECH-968). Nothing to prefill
 * for a family with an adult signer — `legalRepresentative` is only ever set on all-minor ones.
 */
function signatureDefaults(receivable: {
  passengers: ProposableSigner[];
  legalRepresentative?: { firstName: string; lastName: string; email: string } | null;
}): SignatureFormState {
  const representative = receivable.legalRepresentative;

  return {
    // The legal representative's address for an all-minor family, otherwise the proposed signer's own —
    // the two branches share this one field, and only one of them is ever rendered.
    email: representative?.email ?? defaultSigner(receivable)?.email ?? "",
    signerFullName: defaultSignerFullName(receivable),
    legalFirstName: representative?.firstName ?? "",
    legalLastName: representative?.lastName ?? "",
  };
}

/** Port of states-cta/ReceivablesContractsCTA.vue — génération et envoi des cessions de créance. */
export function ReceivablesContractsCTA({ claim }: { claim: ClaimWithRelations }) {
  const { can } = useCurrentUser();

  const { data: contactEmails } = useClaimContactEmails(claim.id);
  const { data: contacts } = usePnrContacts(claim.id);
  const { data: receivables = [], isPending: evaluating } = useReceivableContracts(claim.id);

  const generateReceivables = useGenerateReceivables(claim.id);
  const notifyReceivablesReady = useNotifyReceivablesReady(claim.id);
  const launchAcquisition = useLaunchFamilyAcquisitionWorkflow(claim.id);

  const reservation = claim.legalSpecification.impactedSegment.reservation;
  const feedback = claim.agentFeedback;

  // Email to notify — mirrors getTravelAgencyEmail (Vue)
  const isBusinessTravelForEmail = Boolean(
    reservation.travelAgencyCompany ||
      feedback?.b2c === false ||
      (!feedback && reservation.travelAgencyOffice.isBusinessTravel),
  );
  const defaultEmail = contactEmails
    ? ((isBusinessTravelForEmail
        ? contactEmails.accountManagerEmail || contactEmails.fallbackOfficeEmail
        : contactEmails.reservationAgentEmail || contactEmails.fallbackOfficeEmail) ?? "")
    : "";

  const [email, setEmail] = useState(defaultEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const effectiveEmail = emailTouched ? email : defaultEmail;

  const isBusinessTravel = Boolean(reservation.travelAgencyCompany || feedback?.b2c === false);
  // Families can only be reshaped before the receivables are generated (one contract per family),
  // and only when there is someone to move: a lone passenger has no family to change.
  const isFamilyEditable =
    claim.claimState.type === "CLAIM_VALIDATION.CHOICE_OF_CONTACT_MODE" && claim.usersClaims.length > 1;
  const isDirectSignatureAllowed = isBusinessTravel
    ? Boolean(reservation.travelAgencyCompany?.isDirectPassengerContactAllowed)
    : true;

  // Per-receivable signature form (keyed by boxFileId)
  const [signatureForm, setSignatureForm] = useState<Record<string, SignatureFormState>>({});
  const [sendingFileId, setSendingFileId] = useState<string | null>(null);
  // A send waiting on the jurist: its address already reaches another family of the claim.
  const [sharedEmailSend, setSharedEmailSend] = useState<{
    receivable: (typeof receivables)[number];
    familyName: string;
  } | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the forms when switching claim
  useEffect(() => {
    setSignatureForm({});
    setEmailTouched(false);
  }, [claim.id]);

  function openBoxSharedFolder() {
    const boxFolderId = claim.boxFolderIdGeneratedRecevables;
    if (!boxFolderId) {
      notify.error("Le dossier Box partagé n'est pas encore initialisé");
      return;
    }
    window.open(`https://upclaim.app.box.com/folder/${boxFolderId}`, "_blank")?.focus();
  }

  function updateSignatureForm(
    boxFileId: string,
    patch: Partial<SignatureFormState>,
    defaults: SignatureFormState,
  ) {
    setSignatureForm((current) => ({
      ...current,
      [boxFileId]: { ...defaults, ...current[boxFileId], ...patch },
    }));
  }

  async function sendSignature(receivable: (typeof receivables)[number], confirmedSharedEmail = false) {
    // Untouched form means the jurist accepted the prefill as-is — send exactly what they saw.
    const form = signatureForm[receivable.boxFileId] ?? signatureDefaults(receivable);
    const familyId = receivable.passengers[0]?.familyId;
    if (!familyId) {
      return;
    }

    // Couples and families often share one mailbox: allowed, but only once the jurist has said so.
    const sharingFamily = confirmedSharedEmail
      ? undefined
      : findFamilyUsingEmail(claim.usersClaimsFamilies, form.email, familyId);
    if (sharingFamily) {
      setSharedEmailSend({ receivable, familyName: sharingFamily.familyName ?? "" });
      return;
    }

    setSendingFileId(receivable.boxFileId);
    try {
      await launchAcquisition.mutateAsync(
        isMinorsOnly(receivable)
          ? {
              familyId,
              boxFileId: receivable.boxFileId,
              legalRepresentative: {
                firstName: form.legalFirstName,
                lastName: form.legalLastName,
                email: form.email,
              },
            }
          : {
              familyId,
              boxFileId: receivable.boxFileId,
              signerFullName: form.signerFullName || defaultSignerFullName(receivable),
              signerEmail: form.email,
            },
      );
    } finally {
      setSendingFileId(null);
    }
  }

  function isReadyToSend(
    receivable: (typeof receivables)[number],
    form: SignatureFormState | undefined,
  ): boolean {
    if (!isValidEmail(form?.email)) {
      return false;
    }

    return isMinorsOnly(receivable)
      ? Boolean(form?.legalFirstName?.trim() && form?.legalLastName?.trim())
      : true;
  }

  const items: Array<{
    name: string;
    description: ReactNode;
    Icon: typeof Cog;
    cta: { text: string; onClick: () => void; tooltip?: string | null };
  }> = [
    {
      name: "Générer les cessions",
      description: "Vérifier les montants MC99. Une fois générés, les document sont accessibles sur Box.",
      Icon: Cog,
      cta: {
        text: "Générer",
        onClick: () => generateReceivables.mutate(),
        tooltip: claim.boxFolderIdGeneratedRecevables ? "Les cessions ont déjà été générées" : null,
      },
    },
    {
      name: "Vérifier les cessions",
      description: "Une fois générés, les document sont accessibles sur Box.",
      Icon: ExternalLink,
      cta: {
        text: "Vérifier",
        onClick: openBoxSharedFolder,
      },
    },
    {
      name: "Transmettre les cessions à l'agence",
      description: "Email à l'agent ou au chargé de compte avec les documents à faire signer.",
      Icon: Mail,
      cta: {
        text: "Envoyer",
        onClick: () => {
          if (effectiveEmail) {
            notifyReceivablesReady.mutate(effectiveEmail);
          }
        },
      },
    },
  ];

  // One cession per family: the families and the PNR contacts are read just before asking the passengers.
  const familiesSection = (
    <div className="pt-6">
      <h3 className="text-base font-semibold text-gray-900">Familles et contacts</h3>
      <p className="mt-1 mb-4 text-sm text-gray-500">
        Une cession est générée par famille.
        {isFamilyEditable
          ? " Glissez un passager sur une autre famille pour le déplacer, ou extrayez-le en un clic."
          : null}
      </p>
      <FamilyBoard claim={claim} editable={isFamilyEditable} />
      <PnrContacts contacts={contacts ?? []} familyByEmail={familyByEmail(claim)} />
    </div>
  );

  return (
    <CtaShell legalStepName={claim.claimLegalStep.name}>
      <p className="mt-1 text-sm text-gray-500">
        Génération, vérification et envoie des cessions de créances.
      </p>

      {can(UPCLAIM_ROLES) ? (
        <div className="pt-6">
          {!isDirectSignatureAllowed ? (
            <Alert>
              <TriangleAlert className="text-amber-500" />
              <AlertTitle>Contact direct des voyageurs non autorisé</AlertTitle>
              <AlertDescription>
                <p>
                  {isBusinessTravel ? "Le chargé de comptes" : "L'agent"} s'occupe de transmettre et d'obtenir
                  les signatures.
                </p>
                <p className="mt-2">
                  Email envoyé à « <b>{effectiveEmail}</b> »
                </p>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <Info className="text-blue-500" />
              <AlertTitle>Contact direct des voyageurs autorisé</AlertTitle>
              <AlertDescription>
                Ou envoie pour signature manuelle à «<u>{effectiveEmail}</u>»
              </AlertDescription>
            </Alert>
          )}
        </div>
      ) : null}

      <div className="w-full py-6">
        <Label
          htmlFor="manual-send-email"
          className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700"
        >
          <AtSign className="h-4 w-4 text-gray-400" />
          Adresse pour envoi manuel
        </Label>
        <Input
          id="manual-send-email"
          value={effectiveEmail}
          onChange={(event) => {
            setEmailTouched(true);
            setEmail(event.target.value);
          }}
        />
      </div>

      <ul className="mt-6 divide-y divide-gray-200 border-t border-b border-gray-200">
        {items.map((item, itemIdx) => (
          <li key={item.name}>
            <div className="group pointer-events-auto relative flex items-start space-x-6 py-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900">
                  <p>
                    {itemIdx + 1}. {item.name}
                  </p>
                </div>
                <div className="text-sm text-gray-500">
                  <p>{item.description}</p>
                </div>
              </div>
              <div className="flex-shrink-0 self-center">
                <button
                  type="button"
                  title={item.cta.tooltip ?? undefined}
                  onClick={item.cta.onClick}
                  className="inline-flex cursor-pointer items-center gap-x-1.5 rounded bg-white px-2 py-1.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-300 ring-inset group-hover:text-gray-900 hover:bg-gray-50"
                >
                  <item.Icon className="h-4 w-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                  {item.cta.text}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {!claim.boxFolderIdGeneratedRecevables ? familiesSection : null}

      {claim.boxFolderIdGeneratedRecevables ? (
        <div className="relative mt-6 block text-sm">
          <h3 className="mb-2 leading-7 font-medium text-gray-900">Demandes de signatures</h3>

          <div className="px-2">{familiesSection}</div>

          <div className="mt-8 block px-2">
            <p className="inline-flex items-center text-sm leading-6 font-medium text-gray-700">
              <span className="inline-flex">
                <HelpCircle className="mr-2 h-5 w-5 flex-shrink-0 text-gray-400" />
                Question à destination des passagers ?
              </span>
            </p>
            <div className="mt-2 text-sm text-gray-900">
              <PassengerQuestionsEditor key={claim.id} claim={claim} />
            </div>
          </div>

          <div className="my-3 mt-4 rounded border py-6">
            {!receivables.length ? (
              <div className="text-center">
                {evaluating ? (
                  <Spinner className="mx-auto size-5 text-gray-400" />
                ) : (
                  <p className="inline-flex items-center">
                    <span className="mr-2 text-gray-500">
                      <Info size={16} />
                    </span>
                    Aucune cession de créance passager détectée
                  </p>
                )}
              </div>
            ) : null}

            {receivables.map((receivable, _index, all) => {
              // ⌘↵ only when a single contract is left to send: with several, it would not know which one.
              const isOnlyPending = all.filter((item) => !item.signatureRequest?.createdAt).length === 1;
              const defaults = signatureDefaults(receivable);
              const minorsOnly = isMinorsOnly(receivable);
              const form = signatureForm[receivable.boxFileId] ?? defaults;
              return (
                <div key={receivable.boxFileId}>
                  <div className="inline-flex align-middle">
                    <span className="px-4">
                      <FileSignature />
                    </span>
                    <div className="block leading-6">
                      <p>
                        Cession «{" "}
                        <a
                          href={`https://upclaim.app.box.com/file/${receivable.boxFileId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline hover:text-blue-800"
                        >
                          {receivable.boxFileName}
                        </a>{" "}
                        »
                      </p>
                      <p>
                        Passager(s) :{" "}
                        <span>{receivable.passengers.map(({ fullName }) => fullName).join(", ")}</span>
                      </p>
                      {!receivable.signatureRequest?.createdAt ? (
                        <div className="space-y-2 pr-6">
                          {minorsOnly ? (
                            <Alert variant="warning">
                              <TriangleAlert />
                              <AlertTitle>Tous les passagers de cette famille sont mineurs</AlertTitle>
                              <AlertDescription>
                                La cession doit être signée par leur représentant légal (parent ou tuteur).
                                Renseignez son identité ci-dessous — c'est à lui que la demande de signature
                                sera envoyée, et sa pièce d'identité lui sera demandée en plus de celles des
                                passagers.
                              </AlertDescription>
                            </Alert>
                          ) : null}
                          {minorsOnly ? (
                            <>
                              <Input
                                placeholder="Prénom du représentant légal"
                                value={form.legalFirstName}
                                onChange={(event) =>
                                  updateSignatureForm(
                                    receivable.boxFileId,
                                    { legalFirstName: event.target.value },
                                    defaults,
                                  )
                                }
                              />
                              <Input
                                placeholder="Nom du représentant légal"
                                value={form.legalLastName}
                                onChange={(event) =>
                                  updateSignatureForm(
                                    receivable.boxFileId,
                                    { legalLastName: event.target.value },
                                    defaults,
                                  )
                                }
                              />
                              <Input
                                placeholder="Email du représentant légal"
                                value={form.email}
                                onChange={(event) =>
                                  updateSignatureForm(
                                    receivable.boxFileId,
                                    { email: event.target.value },
                                    defaults,
                                  )
                                }
                              />
                            </>
                          ) : (
                            <>
                              <Input
                                placeholder="Adresse email"
                                value={form.email}
                                onChange={(event) =>
                                  updateSignatureForm(
                                    receivable.boxFileId,
                                    { email: event.target.value },
                                    defaults,
                                  )
                                }
                              />
                              <Input
                                placeholder="Nom complet du signataire"
                                value={form.signerFullName}
                                onChange={(event) =>
                                  updateSignatureForm(
                                    receivable.boxFileId,
                                    { signerFullName: event.target.value },
                                    defaults,
                                  )
                                }
                              />
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2 inline-flex items-center text-sm text-gray-500 italic">
                          <span className="mr-2 text-green-700">
                            <BadgeCheck size={16} />
                          </span>
                          Signature envoyée il y a{" "}
                          {formatDistanceToNow(new Date(receivable.signatureRequest.createdAt), {
                            locale: fr,
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  {!receivable.signatureRequest?.createdAt ? (
                    <div className="mt-3 inline-flex px-14">
                      <ShortcutButton
                        shortcut={isOnlyPending}
                        onClick={() => void sendSignature(receivable)}
                        disabled={!isReadyToSend(receivable, form)}
                        loading={sendingFileId === receivable.boxFileId}
                      >
                        Envoyer la signature
                      </ShortcutButton>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <SharedEmailConfirmDialog
        familyName={sharedEmailSend?.familyName ?? null}
        onCancel={() => setSharedEmailSend(null)}
        onConfirm={() => {
          if (sharedEmailSend) {
            setSharedEmailSend(null);
            void sendSignature(sharedEmailSend.receivable, true);
          }
        }}
      />
    </CtaShell>
  );
}
